/**
 * City Agency Pages — Wave 13
 * Tianjin, Qingdao (China), Chennai (India), Islamabad (Pakistan),
 * Casablanca-Nearshore (duplicate avoided → Rabat), Abuja (Nigeria),
 * Guadalajara (Mexico), Lyon (France), Munich (Germany), Edinburgh (UK)
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web apps, portals, dashboards using React, TypeScript, and cloud infrastructure.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, monitoring.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance.' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools with enterprise-grade security.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations, HIPAA-compliant apps.' },
  { name: 'SaaS & Technology', description: 'Product development, scaling, and modernization for SaaS companies.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, tenant portals.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE13: Record<string, CityAgencyData> = {

  tianjin: {
    slug: 'software-development-agency-tianjin',
    cityName: 'Tianjin',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY',
    title: {
      en: 'Software Development Agency Tianjin | Call IT Dev',
      fr: 'Agence Développement Logiciel Tianjin | Call IT Dev',
      zh: '天津软件开发公司 | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Tianjin, China. Smart port tech, AI, and enterprise systems. Adjacent to Beijing, from $18/hr.',
      fr: 'Agence de développement logiciel à Tianjin, Chine. Tech portuaire, IA, systèmes d\'entreprise, dès 18 $/h.',
      zh: '天津软件开发公司。智慧港口技术、AI和企业系统。毗邻北京，18美元/小时起。',
    },
    keywords: ['software development Tianjin', '天津软件开发', 'IT outsourcing Tianjin', 'smart port tech China'],
    heroTitle: {
      en: 'Software Development Agency in Tianjin',
      fr: 'Agence de Développement Logiciel à Tianjin',
      zh: '天津软件开发公司',
    },
    heroSubtitle: {
      en: "China's gateway port city. Smart logistics, AI, and enterprise systems. 30 min HSR from Beijing, 25% lower costs than the capital.",
      fr: "Ville portuaire de Chine. Logistique intelligente, IA, systèmes d'entreprise. 30 min de Pékin, 25% moins cher.",
      zh: '中国门户港口城市。智慧物流、AI和企业系统。距北京高铁30分钟，成本低25%。',
    },
    whyTitle: {
      en: 'Why Tianjin for Software Development',
      fr: 'Pourquoi Tianjin pour le Développement Logiciel',
      zh: '为什么选择天津进行软件开发',
    },
    whyReasons: [
      { icon: '🚢', title: 'Smart Port Hub', description: 'Tianjin Port is the world\'s 8th busiest. Deep expertise in port automation and logistics tech.' },
      { icon: '🚄', title: '30 Min from Beijing', description: 'High-speed rail to Beijing in 30 min. Access to Beijing ecosystem at Tianjin prices.' },
      { icon: '💰', title: '25% Below Beijing', description: 'Significantly lower costs than Beijing while maintaining quality standards.' },
      { icon: '🎓', title: 'Nankai & Tianjin University', description: 'Two top-tier universities producing excellent STEM graduates.' },
      { icon: '🏗️', title: 'Binhai New Area', description: 'Binhai Hi-Tech Zone hosts hundreds of tech companies and R&D centers.' },
      { icon: '🤖', title: 'AI & Robotics', description: 'Strong ecosystem in AI, robotics, and intelligent manufacturing.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Tianjin over Beijing?', answer: '25% lower costs, lower attrition, 30-minute HSR to Beijing. Same talent quality at better prices.' },
      { question: 'What specializations?', answer: 'Smart port technology, logistics automation, AI/ML, enterprise systems, and IoT.' },
      { question: 'How fast can you start?', answer: 'Team operational in 2-3 weeks with CVs in 5 business days.' },
      { question: 'Data security?', answer: 'Enterprise Security Standards, compliant with Chinese cybersecurity law and international standards.' },
      { question: 'Can you scale?', answer: 'Start with 2 developers, scale to 30+ leveraging Tianjin and Beijing talent pools.' },
      { question: 'IP protection?', answer: 'Full IP assignment, NDAs, and source code ownership transfer.' },
    ],
    localRate: '¥400-600',
    ourRate: '$18-30',
    savings: '55%+',
  },

  qingdao: {
    slug: 'software-development-agency-qingdao',
    cityName: 'Qingdao',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY',
    title: {
      en: 'Software Development Agency Qingdao | Call IT Dev',
      fr: 'Agence Développement Logiciel Qingdao | Call IT Dev',
      zh: '青岛软件开发公司 | Call IT Dev',
      ko: '칭다오 소프트웨어 개발 회사 | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Qingdao, China. Korea-facing IT hub, marine tech, smart manufacturing. From $16/hr.',
      fr: 'Agence de développement logiciel à Qingdao, Chine. Hub IT Corée, tech marine, dès 16 $/h.',
      zh: '青岛软件开发公司。面向韩国的IT中心，海洋科技，智能制造。16美元/小时起。',
      ko: '칭다오 소프트웨어 개발 회사. 한국 대상 IT 허브, 해양 기술, 스마트 제조. 시간당 16달러부터.',
    },
    keywords: ['software development Qingdao', '青岛软件开发', '칭다오 소프트웨어', 'IT outsourcing Qingdao'],
    heroTitle: {
      en: 'Software Development Agency in Qingdao',
      fr: 'Agence de Développement Logiciel à Qingdao',
      zh: '青岛软件开发公司',
      ko: '칭다오 소프트웨어 개발 회사',
    },
    heroSubtitle: {
      en: "China's Korea-facing tech hub. Korean/Chinese bilingual engineers, marine tech & smart manufacturing expertise. Direct flights to Seoul.",
      fr: "Hub tech chinois tourné vers la Corée. Ingénieurs bilingues coréen/chinois, tech marine.",
      zh: '中国面向韩国的技术中心。中韩双语工程师，海洋科技和智能制造专长。',
      ko: '중국의 한국 대상 기술 허브. 한중 이중 언어 엔지니어, 해양 기술 및 스마트 제조 전문성.',
    },
    whyTitle: {
      en: 'Why Qingdao for Software Development',
      fr: 'Pourquoi Qingdao pour le Développement Logiciel',
      zh: '为什么选择青岛进行软件开发',
      ko: '왜 칭다오에서 소프트웨어 개발을 해야 하나',
    },
    whyReasons: [
      { icon: '🇰🇷', title: 'Korea IT Hub', description: 'Qingdao hosts 5,000+ Korean companies. Large pool of Korean-speaking engineers.' },
      { icon: '🌊', title: 'Marine Tech', description: 'Deep expertise in marine technology, ocean monitoring, and maritime logistics.' },
      { icon: '💰', title: '35% Below Tier 1', description: 'Significantly lower than Beijing/Shanghai with quality talent.' },
      { icon: '🏭', title: 'Smart Manufacturing', description: 'Haier and other major manufacturers drive strong Industry 4.0 expertise.' },
      { icon: '✈️', title: 'Direct to Seoul', description: 'Direct flights to Seoul (1.5h), Tokyo (2.5h). Easy for APAC collaboration.' },
      { icon: '🎓', title: 'University Pipeline', description: 'Ocean University of China and Shandong University Qingdao campus.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why is Qingdao popular with Korean companies?', answer: 'Geographic proximity (1.5h flight to Seoul), 5,000+ Korean businesses, and large Korean-speaking workforce.' },
      { question: 'What specializations?', answer: 'Marine tech, smart manufacturing (Haier ecosystem), Korean market apps, and enterprise systems.' },
      { question: 'Cost comparison?', answer: '35% below Beijing/Shanghai rates with strong quality from local universities.' },
      { question: 'Korean language support?', answer: 'Yes. Large pool of TOPIK-certified Korean-speaking engineers available.' },
      { question: 'How fast can you start?', answer: 'Team setup in 2-3 weeks. Korean-language projects can start faster.' },
      { question: 'Data protection?', answer: 'Enterprise Security Standards, Chinese cybersecurity law compliance, and Korean PIPA alignment.' },
    ],
    localRate: '¥350-550',
    ourRate: '$16-28',
    savings: '60%+',
  },

  chennai: {
    slug: 'software-development-agency-chennai',
    cityName: 'Chennai',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (GMT+5:30)',
    currency: 'INR/USD',
    title: {
      en: 'Software Development Agency Chennai | Call IT Dev',
      fr: 'Agence Développement Logiciel Chennai | Call IT Dev',
      de: 'Softwareentwicklung Agentur Chennai | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Chennai, India. Automotive & ERP hub, 1.5M+ IT workforce. From $14/hr.',
      fr: 'Agence de développement logiciel à Chennai, Inde. Hub automobile & ERP, 1,5M+ professionnels IT, dès 14 $/h.',
      de: 'Softwareentwicklungsagentur in Chennai, Indien. Automobil- & ERP-Hub, 1,5M+ IT-Fachkräfte, ab 14 $/h.',
    },
    keywords: ['software development Chennai', 'IT outsourcing Chennai', 'web development Chennai India', 'ERP development Chennai'],
    heroTitle: {
      en: 'Software Development Agency in Chennai',
      fr: 'Agence de Développement Logiciel à Chennai',
      de: 'Softwareentwicklung Agentur in Chennai',
    },
    heroSubtitle: {
      en: "India's Detroit — automotive tech meets IT powerhouse. 1.5M+ tech professionals, deep ERP & SAP expertise, 70% savings vs Western rates.",
      fr: "Le Détroit de l'Inde — tech auto et puissance IT. 1,5M+ professionnels tech, expertise ERP & SAP, 70% d'économies.",
      de: 'Indiens Detroit — Automobiltechnologie trifft IT-Powerhouse. 1,5M+ Tech-Profis, ERP & SAP-Expertise, 70% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Chennai for Software Development',
      fr: 'Pourquoi Chennai pour le Développement Logiciel',
      de: 'Warum Chennai für Softwareentwicklung',
    },
    whyReasons: [
      { icon: '🚗', title: 'Automotive Tech Hub', description: 'Hyundai, BMW, Ford R&D centers. Deep automotive embedded and IoT expertise.' },
      { icon: '💰', title: '70% Cost Savings', description: '$14-26/hr vs $80-150/hr in US/EU. Chennai rates are 10% below Bangalore.' },
      { icon: '🏢', title: 'IT Corridor', description: 'Tidel Park, SIPCOT IT Park host TCS, Cognizant, Infosys HQs.' },
      { icon: '📊', title: 'ERP & SAP Capital', description: 'Largest concentration of SAP/Oracle ERP consultants in India.' },
      { icon: '🗣️', title: 'English Fluent', description: 'All engineers fluent in English. Strong communication skills.' },
      { icon: '🎓', title: 'IIT Madras', description: 'India\'s #1 ranked engineering institute. World-class talent pipeline.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Chennai over Bangalore?', answer: 'Chennai offers 10-15% lower costs, lower attrition, strong automotive/ERP specialization, and IIT Madras talent.' },
      { question: 'What ERP systems do you support?', answer: 'SAP S/4HANA, Oracle EBS, Microsoft Dynamics, Salesforce, and custom ERP solutions.' },
      { question: 'Timezone overlap with Europe?', answer: 'IST is GMT+5:30. 3-4 hours overlap with CET. Flexible schedules available.' },
      { question: 'Data protection?', answer: 'Enterprise-grade security, GDPR compliant. India\'s DPDP Act compliance.' },
      { question: 'Can you handle automotive projects?', answer: 'Yes. Deep expertise in AUTOSAR, embedded systems, V2X, and automotive IoT.' },
      { question: 'Team setup timeline?', answer: '2-3 weeks for initial team. Rapid scaling from Chennai\'s massive talent pool.' },
    ],
    localRate: '$60-100',
    ourRate: '$14-26',
    savings: '70%+',
  },

  islamabad: {
    slug: 'software-development-agency-islamabad',
    cityName: 'Islamabad',
    countryName: 'Pakistan',
    flag: '🇵🇰',
    timezone: 'PKT (GMT+5)',
    currency: 'PKR/USD',
    title: {
      en: 'Software Development Agency Islamabad | Call IT Dev',
      fr: 'Agence Développement Logiciel Islamabad | Call IT Dev',
      ar: 'شركة تطوير برمجيات إسلام آباد | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Islamabad, Pakistan. Top NUST/FAST talent, fintech & govtech. From $12/hr.',
      fr: 'Agence de développement logiciel à Islamabad, Pakistan. Talents NUST/FAST, fintech & govtech, dès 12 $/h.',
      ar: 'شركة تطوير برمجيات في إسلام آباد. خريجو NUST وFAST، فنتك وتقنية حكومية، من 12$/ساعة.',
    },
    keywords: ['software development Islamabad', 'IT outsourcing Pakistan', 'web development Islamabad', 'fintech Pakistan'],
    heroTitle: {
      en: 'Software Development Agency in Islamabad',
      fr: 'Agence de Développement Logiciel à Islamabad',
      ar: 'شركة تطوير برمجيات في إسلام آباد',
    },
    heroSubtitle: {
      en: "Pakistan's capital & tech hub. NUST and FAST university graduates, fintech & govtech expertise, 80% savings vs Western rates.",
      fr: "Capitale tech du Pakistan. Diplômés NUST et FAST, expertise fintech & govtech, 80% d'économies.",
      ar: 'عاصمة باكستان التقنية. خريجو NUST وFAST، خبرة فنتك وتقنية حكومية، توفير 80%.',
    },
    whyTitle: {
      en: 'Why Islamabad for Software Development',
      fr: 'Pourquoi Islamabad pour le Développement Logiciel',
      ar: 'لماذا إسلام آباد لتطوير البرمجيات',
    },
    whyReasons: [
      { icon: '🎓', title: 'NUST & FAST', description: 'Pakistan\'s top engineering universities produce 50K+ CS graduates annually.' },
      { icon: '💰', title: '80% Cost Savings', description: '$12-22/hr vs $80-150/hr in US/UK. Exceptional value.' },
      { icon: '🏦', title: 'Fintech Hub', description: 'JazzCash, Easypaisa, and 100+ fintech startups drive mobile money expertise.' },
      { icon: '🗣️', title: 'English Proficient', description: 'English is the business language. All engineers communicate fluently.' },
      { icon: '🏙️', title: 'Planned City', description: 'Islamabad\'s planned infrastructure provides reliable power, internet, and offices.' },
      { icon: '⏰', title: 'EU/ME Overlap', description: 'PKT (GMT+5) overlaps with Middle East and has 3-4h overlap with CET.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Islamabad over Karachi/Lahore?', answer: 'Planned city with best infrastructure, proximity to NUST, lower attrition, and more stable environment.' },
      { question: 'What about internet reliability?', answer: 'Islamabad has Pakistan\'s best internet infrastructure. Our offices have redundant fiber and backup connectivity.' },
      { question: 'Fintech expertise?', answer: 'Deep expertise in mobile money, Islamic fintech, payment gateways, and banking systems.' },
      { question: 'Data protection?', answer: 'Compliant with Pakistan\'s PECA and international data protection standards.' },
      { question: 'Can you work with Gulf clients?', answer: 'Yes. PKT aligns perfectly with GCC timezones. Many engineers speak Arabic.' },
      { question: 'Team setup?', answer: '2 weeks to operational. NUST/FAST pipeline enables rapid scaling.' },
    ],
    localRate: '$50-100',
    ourRate: '$12-22',
    savings: '80%+',
  },

  rabat: {
    slug: 'software-development-agency-rabat',
    cityName: 'Rabat',
    countryName: 'Morocco',
    flag: '🇲🇦',
    timezone: 'CET (GMT+1)',
    currency: 'MAD/EUR',
    title: {
      en: 'Software Development Agency Rabat | Call IT Dev',
      fr: 'Agence de Développement Informatique Rabat | Call IT Dev',
      ar: 'وكالة تطوير برمجيات الرباط | Call IT Dev',
      es: 'Agencia Desarrollo Software Rabat | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Rabat, Morocco. Government tech & smart city hub. French/English/Arabic engineers, from $22/hr.',
      fr: 'Agence de développement informatique à Rabat, Maroc. Hub govtech & ville intelligente, dès 22 $/h.',
      ar: 'وكالة تطوير برمجيات في الرباط. مركز تقنية حكومية ومدينة ذكية. من 22$/ساعة.',
      es: 'Agencia de desarrollo de software en Rabat, Marruecos. Hub govtech y ciudad inteligente, desde 22 $/h.',
    },
    keywords: ['software development Rabat', 'agence développement informatique Rabat', 'nearshore development Morocco', 'govtech Morocco'],
    heroTitle: {
      en: 'Software Development Agency in Rabat',
      fr: 'Agence de Développement Informatique à Rabat',
      ar: 'وكالة تطوير برمجيات في الرباط',
      es: 'Agencia de Desarrollo de Software en Rabat',
    },
    heroSubtitle: {
      en: "Morocco's capital & govtech hub. Smart city projects, e-government platforms, trilingual engineers. CET timezone, 60% savings.",
      fr: "Capitale du Maroc et hub govtech. Projets smart city, e-gouvernement, ingénieurs trilingues. Fuseau CET, 60% d'économies.",
      ar: 'عاصمة المغرب ومركز التقنية الحكومية. مشاريع مدينة ذكية، حكومة إلكترونية، مهندسون ثلاثيو اللغة.',
      es: 'Capital de Marruecos y hub govtech. Proyectos smart city, e-gobierno, ingenieros trilingües. 60% ahorro.',
    },
    whyTitle: {
      en: 'Why Rabat for Software Development',
      fr: 'Pourquoi Rabat pour le Développement Logiciel',
      ar: 'لماذا الرباط لتطوير البرمجيات',
      es: 'Por Qué Rabat para Desarrollo de Software',
    },
    whyReasons: [
      { icon: '🏛️', title: 'Government Tech Hub', description: 'Morocco\'s capital hosts all ministries and e-government digitization projects.' },
      { icon: '🎓', title: 'Mohammed V University', description: 'Morocco\'s top-ranked university produces elite engineers and researchers.' },
      { icon: '🌐', title: 'Trilingual Teams', description: 'Engineers fluent in French, English, and Arabic. Seamless European & MENA communication.' },
      { icon: '💰', title: '60% Cost Savings', description: '€22-38/hr vs €80-120/hr in Western Europe. Premium talent at nearshore rates.' },
      { icon: '🏗️', title: 'Technopolis Rabat', description: 'Morocco\'s premier tech park with world-class infrastructure and connectivity.' },
      { icon: '✈️', title: '2.5h to Europe', description: 'Direct flights to Paris, Madrid, Brussels, Amsterdam. Easy for client visits.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does Rabat compare to Casablanca?', answer: 'Rabat is Morocco\'s government and research hub; Casablanca is the business hub. Rabat excels in govtech and academic partnerships.' },
      { question: 'What govtech projects do you handle?', answer: 'E-government platforms, citizen portals, smart city dashboards, public health systems, and digital identity.' },
      { question: 'Languages supported?', answer: 'Native French, fluent English, native Arabic. Documentation and communication in all three.' },
      { question: 'Cost comparison?', answer: '€22-38/hr vs €80-120/hr in Paris. 60% savings with CET timezone alignment.' },
      { question: 'GDPR compliance?', answer: 'Morocco has an EU adequacy decision. Enterprise-grade security, CNDP compliant.' },
      { question: 'How fast can you start?', answer: 'Team operational in 2 weeks. Strong Rabat-Casablanca talent corridor.' },
    ],
    localRate: '€80-120',
    ourRate: '€22-38',
    savings: '60%+',
  },

  abuja: {
    slug: 'software-development-agency-abuja',
    cityName: 'Abuja',
    countryName: 'Nigeria',
    flag: '🇳🇬',
    timezone: 'WAT (GMT+1)',
    currency: 'NGN/USD',
    title: {
      en: 'Software Development Agency Abuja | Call IT Dev',
      fr: 'Agence Développement Logiciel Abuja | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Abuja, Nigeria. Govtech & fintech hub, English-native engineers. From $12/hr.',
      fr: 'Agence de développement logiciel à Abuja, Nigeria. Hub govtech & fintech, ingénieurs anglophones, dès 12 $/h.',
    },
    keywords: ['software development Abuja', 'IT outsourcing Abuja Nigeria', 'govtech Nigeria', 'fintech development Abuja'],
    heroTitle: {
      en: 'Software Development Agency in Abuja',
      fr: 'Agence de Développement Logiciel à Abuja',
    },
    heroSubtitle: {
      en: "Nigeria's capital & govtech center. English-native engineers, government digital transformation expertise, 80% savings.",
      fr: "Capitale du Nigeria et centre govtech. Ingénieurs anglophones natifs, 80% d'économies.",
    },
    whyTitle: {
      en: 'Why Abuja for Software Development',
      fr: 'Pourquoi Abuja pour le Développement Logiciel',
    },
    whyReasons: [
      { icon: '🏛️', title: 'Capital City', description: 'Nigeria\'s federal capital. Hub for government tech contracts and NITDA partnerships.' },
      { icon: '🗣️', title: 'English Native', description: 'English is Nigeria\'s official language. Zero communication barriers.' },
      { icon: '💰', title: '80% Cost Savings', description: '$12-20/hr vs $80-150/hr in US/UK. Exceptional value.' },
      { icon: '🏦', title: 'Fintech Ecosystem', description: 'Nigeria is Africa\'s fintech leader. Deep mobile payment and banking expertise.' },
      { icon: '⏰', title: 'CET Aligned', description: 'WAT (GMT+1) — identical to Central European Time.' },
      { icon: '🏗️', title: 'Stable Infrastructure', description: 'Planned city with reliable power and connectivity infrastructure.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Abuja over Lagos?', answer: 'Abuja offers government tech opportunities, planned infrastructure, lower living costs, and less congestion.' },
      { question: 'What about power reliability?', answer: 'Our office has redundant power supply, backup generators, and fiber internet.' },
      { question: 'Fintech capabilities?', answer: 'Mobile money, digital banking, payment gateways, and regulatory compliance systems.' },
      { question: 'Data security?', answer: 'Enterprise Security Standards, NDPR (Nigeria Data Protection) compliant.' },
      { question: 'Can you scale?', answer: 'Start with 2 developers. Scale leveraging Abuja and Lagos talent pools.' },
      { question: 'Team setup?', answer: 'Operational in 2-3 weeks with CVs in 5 business days.' },
    ],
    localRate: '$50-100',
    ourRate: '$12-20',
    savings: '80%+',
  },

  guadalajara: {
    slug: 'software-development-agency-guadalajara',
    cityName: 'Guadalajara',
    countryName: 'Mexico',
    flag: '🇲🇽',
    timezone: 'CST (GMT-6)',
    currency: 'MXN/USD',
    title: {
      en: 'Software Development Agency Guadalajara | Call IT Dev',
      fr: 'Agence Développement Logiciel Guadalajara | Call IT Dev',
      es: 'Agencia Desarrollo Software Guadalajara | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Guadalajara, Mexico. Mexico\'s Silicon Valley, nearshore to US. From $20/hr.',
      fr: 'Agence de développement logiciel à Guadalajara, Mexique. Silicon Valley mexicaine, nearshore US, dès 20 $/h.',
      es: 'Agencia de desarrollo de software en Guadalajara, México. Silicon Valley mexicano, nearshore EEUU, desde 20 $/h.',
    },
    keywords: ['software development Guadalajara', 'desarrollo software Guadalajara', 'nearshore development Mexico', 'IT outsourcing Guadalajara'],
    heroTitle: {
      en: 'Software Development Agency in Guadalajara',
      fr: 'Agence de Développement Logiciel à Guadalajara',
      es: 'Agencia de Desarrollo de Software en Guadalajara',
    },
    heroSubtitle: {
      en: "Mexico's Silicon Valley. Intel, Oracle, IBM R&D centers. US timezone, bilingual EN/ES engineers, 65% savings vs California.",
      fr: "La Silicon Valley mexicaine. Centres R&D Intel, Oracle, IBM. Fuseau US, ingénieurs bilingues, 65% d'économies.",
      es: 'El Silicon Valley mexicano. Centros I+D Intel, Oracle, IBM. Zona US, ingenieros bilingües, 65% ahorro vs California.',
    },
    whyTitle: {
      en: 'Why Guadalajara for Software Development',
      fr: 'Pourquoi Guadalajara pour le Développement Logiciel',
      es: 'Por Qué Guadalajara para Desarrollo de Software',
    },
    whyReasons: [
      { icon: '🏢', title: 'Mexico\'s Silicon Valley', description: 'Intel, Oracle, IBM, HP all have major R&D centers. 600+ tech companies.' },
      { icon: '⏰', title: 'US Timezone (CST)', description: 'Central Standard Time — perfect overlap with US business hours.' },
      { icon: '💰', title: '65% vs California', description: '$20-35/hr vs $100-180/hr in Silicon Valley. Massive savings.' },
      { icon: '🗣️', title: 'Bilingual EN/ES', description: 'Engineers fluent in English and Spanish. Perfect for US Hispanic market.' },
      { icon: '🎓', title: 'ITESO & UdeG', description: 'Top universities producing 20K+ tech graduates annually.' },
      { icon: '✈️', title: 'US Proximity', description: 'Direct flights to LAX, SFO, DFW, ORD. Easy for onsite collaboration.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Guadalajara over Mexico City?', answer: 'Guadalajara is Mexico\'s tech capital with Intel/Oracle/IBM R&D, lower costs, and a more concentrated tech ecosystem.' },
      { question: 'Timezone overlap with US?', answer: 'CST (GMT-6) provides full business-hours overlap with US Central and partial with East/West coasts.' },
      { question: 'English proficiency?', answer: 'All engineers on US projects are bilingual EN/ES with B2/C1+ English certification.' },
      { question: 'USMCA trade advantages?', answer: 'Yes. Mexico\'s USMCA membership provides favorable trade and IP frameworks for US companies.' },
      { question: 'Data protection?', answer: 'Mexican LFPDPPP (data protection law), Enterprise Security Standards, and US compliance standards.' },
      { question: 'How fast can you start?', answer: 'Team setup in 2 weeks. Guadalajara\'s deep talent pool enables rapid scaling.' },
    ],
    localRate: '$80-140',
    ourRate: '$20-35',
    savings: '65%+',
  },

  lyon: {
    slug: 'software-development-agency-lyon',
    cityName: 'Lyon',
    countryName: 'France',
    flag: '🇫🇷',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Lyon | Call IT Dev',
      fr: 'Agence de Développement Informatique Lyon | Call IT Dev',
      de: 'Softwareentwicklung Agentur Lyon | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Lyon. French-speaking engineers, CET timezone, healthtech & biotech expertise. From €25/hr.',
      fr: 'Agence de développement informatique pour Lyon. Ingénieurs francophones, fuseau CET, expertise healthtech & biotech, dès 25 €/h.',
      de: 'Softwareentwicklungsagentur für Lyon. Französischsprachige Ingenieure, CET-Zeitzone, Healthtech, ab 25 €/h.',
    },
    keywords: ['agence développement informatique Lyon', 'software development Lyon', 'ESN Lyon alternative', 'développeur Lyon nearshore'],
    heroTitle: {
      en: 'Software Development Agency for Lyon',
      fr: 'Agence de Développement Informatique pour Lyon',
      de: 'Softwareentwicklung Agentur für Lyon',
    },
    heroSubtitle: {
      en: "French-speaking engineers at nearshore rates for Lyon businesses. CET timezone, healthtech & biotech expertise, 60% savings vs local agencies.",
      fr: "Ingénieurs francophones à tarifs nearshore pour les entreprises lyonnaises. Fuseau CET, expertise healthtech & biotech, 60% d'économies.",
      de: 'Französischsprachige Ingenieure zu Nearshore-Preisen für Lyoner Unternehmen. CET-Zeitzone, 60% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Lyon Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Lyonnaises Choisissent Call IT Dev',
      de: 'Warum Lyoner Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Native French', description: 'All developers speak native French. No language barrier for Lyon teams.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Identical timezone — real-time collaboration with Lyon offices.' },
      { icon: '💰', title: '60% Cost Savings', description: '€25-40/hr vs €70-100/hr in Lyon. Same quality at nearshore prices.' },
      { icon: '🧬', title: 'Healthtech Expertise', description: 'Lyon\'s biotech corridor demands specialized healthtech solutions. We deliver.' },
      { icon: '✈️', title: '3h Direct Flight', description: 'Casablanca-Lyon: direct flights. Easy for kickoffs and reviews.' },
      { icon: '🏗️', title: 'Scale Fast', description: 'Skip Lyon\'s 3-month recruitment cycle. Get a team in 2 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you serve Lyon specifically?', answer: 'Yes. We have dedicated teams for Lyon clients with native French communication and CET timezone.' },
      { question: 'Healthtech/biotech experience?', answer: 'Yes. Lab management, clinical trial platforms, patient portals, and medical device software.' },
      { question: 'Cost comparison?', answer: '€25-40/hr vs €70-100/hr locally. 60% savings with identical timezone and language.' },
      { question: 'RGPD compliance?', answer: 'Fully RGPD compliant, enterprise-grade security, CNIL guidelines followed.' },
      { question: 'Can you integrate with our Lyon team?', answer: 'Yes. Staff augmentation — your Jira, Slack, Git. Fully embedded in your workflow.' },
      { question: 'Minimum engagement?', answer: 'Start with 2 developers. T&M and fixed-price models available.' },
    ],
    localRate: '€70-100',
    ourRate: '€25-40',
    savings: '60%+',
  },

  munich: {
    slug: 'software-development-agency-munich',
    cityName: 'Munich',
    countryName: 'Germany',
    flag: '🇩🇪',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Munich | Call IT Dev',
      fr: 'Agence Développement Logiciel Munich | Call IT Dev',
      de: 'Softwareentwicklung Agentur München | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Munich. German-speaking engineers, CET timezone, automotive & enterprise. From €25/hr.',
      fr: 'Agence de développement logiciel pour Munich. Ingénieurs germanophones, fuseau CET, auto & entreprise, dès 25 €/h.',
      de: 'Softwareentwicklungsagentur für München. Deutschsprachige Ingenieure, CET-Zeitzone, Automobil & Enterprise, ab 25 €/h.',
    },
    keywords: ['Softwareentwicklung München', 'software development Munich', 'IT outsourcing Munich', 'nearshore development München'],
    heroTitle: {
      en: 'Software Development Agency for Munich',
      fr: 'Agence de Développement Logiciel pour Munich',
      de: 'Softwareentwicklung Agentur für München',
    },
    heroSubtitle: {
      en: "German-speaking engineers at nearshore rates for Munich enterprises. CET timezone, automotive & enterprise tech, 55% savings vs local rates.",
      fr: "Ingénieurs germanophones à tarifs nearshore pour les entreprises munichoises. Fuseau CET, 55% d'économies.",
      de: 'Deutschsprachige Ingenieure zu Nearshore-Preisen für Münchner Unternehmen. CET-Zeitzone, Automobil & Enterprise, 55% Ersparnis.',
    },
    whyTitle: {
      en: 'Why Munich Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Munichoises Choisissent Call IT Dev',
      de: 'Warum Münchner Unternehmen Call IT Dev Wählen',
    },
    whyReasons: [
      { icon: '🗣️', title: 'German-Speaking Team', description: 'Engineers fluent in German for seamless communication with Munich teams.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same timezone as Munich. Real-time collaboration, no compromise.' },
      { icon: '💰', title: '55% Cost Savings', description: '€25-45/hr vs €90-130/hr in Munich. Significant savings for Bavaria.' },
      { icon: '🚗', title: 'Automotive Expertise', description: 'Munich is BMW/Audi/Siemens territory. We build automotive and industrial tech.' },
      { icon: '🏢', title: 'Enterprise Ready', description: 'SAP, Siemens, Allianz — we work with German enterprise standards and processes.' },
      { icon: '🔒', title: 'DSGVO Compliant', description: 'Full German data protection (DSGVO/GDPR) compliance. Enterprise-grade security.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do your engineers speak German?', answer: 'Yes. We have German-speaking engineers (B2/C1+) for Munich client projects.' },
      { question: 'Automotive tech experience?', answer: 'Yes. Connected car, ADAS, V2X, fleet management, and automotive IoT platforms.' },
      { question: 'Cost comparison to Munich?', answer: '€25-45/hr vs €90-130/hr locally. Munich is Germany\'s most expensive tech market.' },
      { question: 'DSGVO compliance?', answer: 'Fully DSGVO/GDPR compliant, enterprise-grade security, with DPA agreements for German clients.' },
      { question: 'Can you integrate with German enterprise processes?', answer: 'Yes. Experience with SAP, Siemens PLM, German automotive ASPICE standards.' },
      { question: 'How fast can you start?', answer: 'Team setup in 2-3 weeks. German-speaking teams may take 3-4 weeks for optimal matching.' },
    ],
    localRate: '€90-130',
    ourRate: '€25-45',
    savings: '55%+',
  },

  edinburgh: {
    slug: 'software-development-agency-edinburgh',
    cityName: 'Edinburgh',
    countryName: 'United Kingdom',
    flag: '🇬🇧',
    timezone: 'GMT/BST',
    currency: 'GBP',
    title: {
      en: 'Software Development Agency Edinburgh | Call IT Dev',
      fr: 'Agence Développement Logiciel Édimbourg | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Edinburgh. English-speaking engineers, fintech & data science. From £20/hr. 65% savings.',
      fr: 'Agence de développement logiciel pour Édimbourg. Ingénieurs anglophones, fintech & data science, dès 20 £/h.',
    },
    keywords: ['software development Edinburgh', 'IT outsourcing Edinburgh', 'fintech development Edinburgh', 'nearshore Scotland'],
    heroTitle: {
      en: 'Software Development Agency for Edinburgh',
      fr: 'Agence de Développement Logiciel pour Édimbourg',
    },
    heroSubtitle: {
      en: "English-speaking engineers for Edinburgh's fintech & data science ecosystem. 1-hour time difference, 65% savings vs Edinburgh rates.",
      fr: "Ingénieurs anglophones pour l'écosystème fintech & data science d'Édimbourg. 1h de décalage, 65% d'économies.",
    },
    whyTitle: {
      en: 'Why Edinburgh Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Édimbourgeoises Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Fluent English', description: 'All engineers speak fluent English. Perfect for Scottish business communication.' },
      { icon: '⏰', title: '1-Hour Difference', description: 'Morocco is GMT+1, just 1 hour ahead. Full overlap for real-time work.' },
      { icon: '💰', title: '65% Cost Savings', description: '£20-35/hr vs £70-120/hr in Edinburgh. Major savings for Scottish companies.' },
      { icon: '🏦', title: 'Fintech Expertise', description: 'Edinburgh is the UK\'s #2 financial center. We build fintech at scale.' },
      { icon: '📊', title: 'Data Science', description: 'Edinburgh\'s AI/ML ecosystem demands data expertise. Our teams deliver.' },
      { icon: '🛡️', title: 'UK GDPR Compliant', description: 'Enterprise-grade security, UK GDPR compliant, ICO-ready.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you serve Scottish companies?', answer: 'Yes. We have dedicated teams for Edinburgh and Glasgow clients with UK GDPR compliance.' },
      { question: 'Fintech experience?', answer: 'Payment platforms, trading systems, wealth management, regulatory reporting, and open banking APIs.' },
      { question: 'Cost comparison?', answer: '£20-35/hr vs £70-120/hr in Edinburgh. 65% savings with 1-hour time difference.' },
      { question: 'UK data compliance?', answer: 'UK GDPR, ICO-ready, enterprise-grade security, DPA agreements for UK clients.' },
      { question: 'Can you integrate with our Edinburgh team?', answer: 'Yes. Staff augmentation model — embedded in your existing workflow.' },
      { question: 'How fast can you start?', answer: 'Team operational in 2 weeks. Immediate access to English-speaking engineers.' },
    ],
    localRate: '£70-120',
    ourRate: '£20-35',
    savings: '65%+',
  },
};
